import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';

import TextInput from 'components/ui/TextInput';
import { IDENTITY_GET_EUROPEAN_VAT_INFORMATION } from 'store/queries';
import { vatCountries, vatNonEUCountries } from 'constants/countries';
import { ActionButton } from 'components/ui/Button';
import { Column, Row } from 'components/shared/Layout';

import {
  ErrorMessage,
  FormContainer,
  StyledH2,
  StyledInputFields,
  StyledLabel,
  StyledPassengerDetails,
  StyledRadio,
  StyledRowFields,
  StyledRowRadioGroup,
  StyledSelect,
  ThalysPassDetailsContainer
} from '../BuyThalysPass.styles';
import { vatSubjectStatus } from '../BuyThalysPass';

const VatValidationForm = ({ vatCountryMismatch }) => {
  const { getValues, setValue, register, unregister, clearErrors, formState } = useFormContext();

  const { errors } = formState;

  const vatEUSubject = getValues().vatSubject === vatSubjectStatus.EU;
  const vatNonEUSubject = getValues().vatSubject === vatSubjectStatus.NON_EU;
  const vatCountry = getValues().vatCountry;
  const vatNumber = getValues().vatNumber;
  const vatValid = getValues().vatValid;

  const [getVatValidation, { data, loading: loadingValidation, error: validationError }] = useLazyQuery(
    IDENTITY_GET_EUROPEAN_VAT_INFORMATION,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const { identityGetEuropeanVatInformation: vatInformation = {} } = data ?? {};

        if (vatInformation?.__typename === 'VatInformation' && vatInformation.valid) {
          setValue('vatValid', true, {
            shouldValidate: true
          });
          setValue('vatValidNumber', `${vatInformation.countryCode}${vatInformation.vatNumber}`, {
            shouldValidate: true
          });
        } else if (vatInformation?.__typename === 'ServiceUnavailableError') {
          setValue('vatValid', false);
          setValue('vatValidNumber', '');
          clearErrors('vatValid');
        } else {
          setValue('vatValid', false, {
            shouldValidate: true
          });
          setValue('vatValidNumber', '', {
            shouldValidate: true
          });
        }
      }
    }
  );

  const { identityGetEuropeanVatInformation: vatResponse = {} } = data ?? {};
  const hasVatInformation = vatResponse?.__typename === 'VatInformation';
  const vatDetails = hasVatInformation ? vatResponse : {};
  const hasValidationError = !hasVatInformation && (validationError || vatResponse?.__typename === 'ServiceUnavailableError');
  const hasError = errors.vatNumber || errors.vatValid || hasValidationError;

  useEffect(() => {
    if (vatEUSubject) {
      register('vatValid', {
        required: true,
        validate: (value) => value
      });
      register('vatValidNumber', {
        required: true
      });
    } else {
      unregister('vatValid');
      unregister('vatValidNumber');
    }
  }, [register, unregister, vatEUSubject]);

  const handleVerifyVat = () => {
    getVatValidation({
      variables: {
        country: getValues().vatCountry,
        number: getValues().vatNumber
      }
    });
  };

  const handleResetValidation = () => {
    setValue('vatValid', false);
    clearErrors('vatValid');
  };

  const handleContinueAction = () => {
    setValue('vatValid', true);
    setValue('vatValidNumber', `${getValues().vatCountry}${getValues().vatNumber}`, {
      shouldValidate: true
    });
    unregister('vatValid');
  };

  return (
    <ThalysPassDetailsContainer>
      <StyledPassengerDetails>
        <StyledH2>
          <FormattedMessage id="thalyspass.vat" />
        </StyledH2>
      </StyledPassengerDetails>
      <FormContainer>
        <StyledInputFields>
          <StyledRowRadioGroup>
            <label htmlFor="vatSubjectNo" data-testid="buy-thalys-pass-vat-subject-no">
              <StyledRadio type="radio" id="vatSubjectNo" value="NO" {...register('vatSubject', { required: true })} />
              <FormattedMessage id="thalyspass.vatSubjectNo" />
            </label>
            <label htmlFor="vatSubjectEU" data-testid="buy-thalys-pass-vat-subject-eu">
              <StyledRadio type="radio" id="vatSubjectEU" value="EU" {...register('vatSubject', { required: true })} />
              <FormattedMessage id="thalyspass.vatSubjectEU" />
            </label>
            <label htmlFor="vatNonEUSubjectNonEU" data-testid="buy-thalys-pass-vat-subject-non-eu">
              <StyledRadio type="radio" id="vatNonEUSubjectNonEU" value="NON_EU" {...register('vatSubject', { required: true })} />
              <FormattedMessage id="thalyspass.vatSubjectNonEU" />
            </label>
          </StyledRowRadioGroup>
        </StyledInputFields>
        {vatEUSubject ? (
          <>
            {hasVatInformation && vatValid && (
              <Column data-testid="buy-thalys-pass-vat-validation-data" span={12} style={{ marginTop: '16px' }}>
                <Row span={12}>
                  <Column span={4}>
                    <b>
                      <FormattedMessage id="thalyspass.vatMemberState" />
                    </b>
                  </Column>
                  <Column span={8}>{vatDetails.countryCode}</Column>
                </Row>
                <Row span={6}>
                  <Column span={4}>
                    <b>
                      <FormattedMessage id="thalyspass.vatNumberWithoutPrefix" />
                    </b>
                  </Column>
                  <Column span={8}>{vatDetails.vatNumber}</Column>
                </Row>
                <Row span={6}>
                  <Column span={4}>
                    <b>
                      <FormattedMessage id="thalyspass.vatRequestDate" />
                    </b>
                  </Column>
                  <Column span={8}>{vatDetails.requestDate}</Column>
                </Row>
                <Row span={6}>
                  <Column span={4}>
                    <b>
                      <FormattedMessage id="thalyspass.name" />
                    </b>
                  </Column>
                  <Column span={8}>{vatDetails.name}</Column>
                </Row>
                <Row span={6}>
                  <Column span={4}>
                    <b>
                      <FormattedMessage id="thalyspass.address" />
                    </b>
                  </Column>
                  <Column span={8}>{vatDetails.address}</Column>
                </Row>
              </Column>
            )}

            {!errors.vatValid && !vatValid && (
              <StyledRowFields style={{ marginTop: '8px' }}>
                <StyledInputFields style={{ maxWidth: '250px' }}>
                  <StyledLabel mandatory htmlFor="passengerCountry">
                    <FormattedMessage id="billingDetailsForm.country" />
                  </StyledLabel>
                  <StyledSelect
                    {...register('vatCountry', {
                      required: true
                    })}
                    id="vatCountry"
                  >
                    <option key="default" disabled selected value=""></option>
                    {vatCountries.map(({ code, name }) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </StyledSelect>
                </StyledInputFields>
                <StyledInputFields style={{ maxWidth: '320px' }}>
                  <StyledLabel mandatory htmlFor="vatNumber">
                    <FormattedMessage id="thalyspass.vatNumberWithoutPrefix" />
                  </StyledLabel>
                  <TextInput
                    id="vatNumber"
                    type="text"
                    placeholder="123456789"
                    {...register('vatNumber', {
                      required: true,
                      pattern: /[0-9A-Za-z\\+\\*\\.]{2,12}/
                    })}
                  />
                </StyledInputFields>
              </StyledRowFields>
            )}
            {hasError && (
              <ErrorMessage style={{ marginTop: '8px' }}>
                {hasValidationError ? (
                  <FormattedMessage id="thalyspass.errors.viesAccess" />
                ) : errors.vatNumber ? (
                  <FormattedMessage id="thalyspass.errors.vatNumber" />
                ) : errors.vatValid ? (
                  <FormattedMessage id="thalyspass.errors.invalidVatNumber" />
                ) : null}
              </ErrorMessage>
            )}
            {vatCountryMismatch && (
              <ErrorMessage style={{ marginTop: '8px' }}>
                <FormattedMessage id="thalyspass.errors.vatMismatch" />
              </ErrorMessage>
            )}
            {hasValidationError ? (
              <ActionButton data-testid="buy-thalys-pass-vat-continue-button" type="button" onClick={handleContinueAction}>
                <FormattedMessage id="errorBoundary.continue" />
              </ActionButton>
            ) : errors.vatValid || vatValid ? (
              <ActionButton type="button" onClick={handleResetValidation}>
                <FormattedMessage id="thalyspass.tryDifferentVat" />
              </ActionButton>
            ) : (
              <ActionButton
                data-testid="buy-thalys-pass-vat-validation-button"
                type="button"
                loading={loadingValidation}
                disabled={loadingValidation || errors.vatNumber || !vatCountry || !vatNumber}
                onClick={handleVerifyVat}
              >
                <FormattedMessage id="thalyspass.verify" />
              </ActionButton>
            )}
          </>
        ) : null}
        {vatNonEUSubject ? (
          <>
            <StyledRowFields style={{ marginTop: '8px' }}>
              <StyledInputFields style={{ maxWidth: '250px' }}>
                <StyledLabel mandatory htmlFor="passengerCountry">
                  <FormattedMessage id="billingDetailsForm.country" />
                </StyledLabel>
                <StyledSelect
                  {...register('vatCountry', {
                    required: true
                  })}
                  id="vatCountry"
                >
                  <option key="default" disabled selected value=""></option>
                  {vatNonEUCountries.map(({ code, name }) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </StyledSelect>
                {errors.vatCountry ? (
                  <ErrorMessage style={{ marginTop: '8px' }}>
                    <FormattedMessage id="billingDetailsForm.incompleteField" />
                  </ErrorMessage>
                ) : null}
                {vatCountryMismatch && (
                  <ErrorMessage style={{ marginTop: '8px' }}>
                    <FormattedMessage id="thalyspass.errors.vatMismatch" />
                  </ErrorMessage>
                )}
              </StyledInputFields>
              <StyledInputFields style={{ maxWidth: '400px' }}>
                <StyledLabel mandatory htmlFor="vatValidNumber">
                  <FormattedMessage id="thalyspass.vatNumberNonEUWithoutPrefix" />
                </StyledLabel>
                <TextInput
                  id="vatNumber"
                  type="text"
                  placeholder="123456789"
                  {...register('vatNumber', {
                    required: true,
                    pattern: /[0-9A-Za-z\\+\\*\\.]{1,20}/
                  })}
                />
                {errors.vatNumber ? (
                  <ErrorMessage style={{ marginTop: '8px' }}>
                    <FormattedMessage id="billingDetailsForm.incompleteField" />
                  </ErrorMessage>
                ) : null}
              </StyledInputFields>
            </StyledRowFields>
          </>
        ) : null}
      </FormContainer>
    </ThalysPassDetailsContainer>
  );
};

VatValidationForm.propTypes = { vatCountryMismatch: PropTypes.bool };

export default VatValidationForm;
